// ui
import React from 'react';
import { Container, Card, Image, Button, LoadingOverlay, Loader, Group, Skeleton, Pagination } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import styles from './styles.module.scss';
import { useCallback, useEffect, useRef, useState } from 'react';

// next
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { setCurrentGame, refreshGameList } from '@/store/features/gameSlice';
import { gameClicked } from "@/utils/event";

export default function GameGrid({ gameList = [], loading = false, currentPage = 1, totalPages = 1, onPageChange, categorySlug }) {
    const router = useRouter();
    const dispatch = useDispatch();
    const imageObserver = useRef(null);
    const [observerReady, setObserverReady] = useState(false);
    const imageCache = useRef(new Set()); // 缓存已加载的图片URL
    const [isVisible, setIsVisible] = useState(false); // 控制容器可见性
    
    const hrefPagination = useCallback((page) => {
        return page === 1 ? `/game/${categorySlug}` : `/game/${categorySlug}/${page}`;
    }, [categorySlug]);

    // 防抖显示内容，避免快速切换时的闪烁
    useEffect(() => {
        if (gameList.length > 0) {
            // 延迟一帧显示内容，确保DOM稳定
            const timer = requestAnimationFrame(() => {
                setIsVisible(true);
            });
            return () => cancelAnimationFrame(timer);
        } else {
            setIsVisible(false);
        }
    }, [gameList.length, categorySlug]);

    // 创建共享的 IntersectionObserver - 确保在组件挂载时立即创建
    useEffect(() => {
        // 确保只创建一次
        if (!imageObserver.current) {
            imageObserver.current = new IntersectionObserver(
                (entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            const img = entry.target;
                            const dataSrc = img.getAttribute('data-src');
                            if (dataSrc) {
                                img.src = dataSrc;
                                img.removeAttribute('data-src');
                                imageObserver.current.unobserve(img);
                            }
                        }
                    });
                },
                {
                    // 添加一些配置选项以提高性能
                    rootMargin: '50px', // 提前50px开始加载
                    threshold: 0.1
                }
            );
            // 标记 observer 已经准备好
            setObserverReady(true);
        }

        return () => {
            if (imageObserver.current) {
                imageObserver.current.disconnect();
                imageObserver.current = null;
            }
            setObserverReady(false);
        };
    }, []); // 保持空依赖数组，确保只执行一次

    const ImageWithLazy = React.memo(({ src, alt, className, gameId }) => {
        // 检查图片是否已经在缓存中
        const isImageCached = imageCache.current.has(src);
        const [isLoaded, setIsLoaded] = useState(isImageCached);
        const imgRef = useRef();

        useEffect(() => {
            // 如果图片已经缓存，直接设置为已加载
            if (isImageCached || isLoaded) {
                return;
            }

            // 确保 imageObserver 已经创建并且 imgRef 已经挂载
            if (imgRef.current && imageObserver.current && src && observerReady) {
                imgRef.current.setAttribute('data-src', src);
                imageObserver.current.observe(imgRef.current);
            }

            return () => {
                if (imgRef.current && imageObserver.current) {
                    imageObserver.current.unobserve(imgRef.current);
                }
            };
        }, [src, observerReady, gameId, isImageCached, isLoaded]); // 添加 isLoaded 作为依赖

        const handleImageLoad = useCallback((e) => {
            // 只有当加载了真实图片时才设置 isLoaded
            if (e.target.src !== "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7") {
                setIsLoaded(true);
                // 将图片URL添加到缓存中
                imageCache.current.add(src);
            }
        }, [src]);

        return (
            <div 
                className={className}
                style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                }}
            >
                {/* Skeleton占位组件 - 只在图片未缓存且未加载时显示 */}
                {!isLoaded && (
                    <Skeleton
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            borderRadius: '24px'
                        }}
                    />
                )}
                
                <img
                    ref={imgRef}
                    src={isLoaded ? src : "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"} // 如果已加载则直接使用真实URL
                    alt={alt}
                    loading={'lazy'}
                    decoding={'async'}
                    className={`${className} ${isLoaded ? styles.loaded : styles.loading}`}
                    onLoad={handleImageLoad}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        opacity: isLoaded ? 1 : 0,
                        transition: 'opacity 0.15s ease-in-out',
                        borderRadius: '24px'
                    }}
                />
            </div>
        );
    }, (prevProps, nextProps) => {
        // 自定义比较函数，减少不必要的重新渲染
        return prevProps.src === nextProps.src && 
               prevProps.gameId === nextProps.gameId;
    });

    // 加载框 - 简化条件判断
    if (loading && !gameList.length) {
        return (
            <Container className={`${styles.container} ${styles.loading}`}>
                <LoadingOverlay
                    visible={true}
                    zIndex={2000}
                    overlayProps={{ radius: 'md', opacity: 0 }}
                    loaderProps={{ color: 'white', type: 'bars' }}
                />
            </Container>
        );
    }

    // 不存在游戏数据时
    if (!gameList.length && !loading) {
        const handleBackHome = () => {
            dispatch(refreshGameList());
        };

        return (
            <Container className={`${styles.container}`}>
                <div className={styles.image_wrapper}>
                    <Image
                        src="/images/icons/common/404.png"
                        alt="404 Error"
                        width={600}
                        height={200}
                        className={styles.error_image}
                    />
                    <div className={styles.remind}>
                        No Data
                    </div>
                </div>
            </Container>
        );
    }

    const handleGameClick = (game) => {
        // TODO： 测试GA的关键事件
        if (window) {
            window.gtag('event', 'click_game');
            window.gtag('event', 'conversion', {
                'send_to': 'AW-17085889980/P17JCLv42coaELz7l9M_'
            });
        }
        // 记录游戏的点击
        gameClicked(game);
        // 保存当前游戏数据到Redux, 避免重复请求
        dispatch(setCurrentGame({...game}));
        // 触发刷新
        dispatch(refreshGameList());
        // 跳转到游戏详情页
        router.push(`/game/${game.slug}`);
    };

    // 使用useMemo来稳定渲染列表，避免不必要的重新渲染
    const memoizedGameList = React.useMemo(() => {
        return gameList.map((game) => ({
            ...game,
            // 确保关键属性稳定 - 包含src确保图片缓存正确工作
            stableKey: `${game.id}`,
            cacheKey: `${game.id}-${game.cover_image}`, // 用于图片缓存的key
        }));
    }, [gameList]);

    return (
        <>
            <div 
                className={styles.grid_container}
                style={{
                    opacity: isVisible ? 1 : 0,
                    transition: 'opacity 0.2s ease-in-out'
                }}
            >
                {memoizedGameList.map((game, index) => (
                    <a
                        title={game.name}
                        key={game.stableKey}
                        href={'/game/' + game.slug}
                        onClick={(e) => {
                            // 只阻止直接点击，允许其他鼠标事件（如右键菜单）
                            if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
                                e.preventDefault();
                                handleGameClick(game);
                            }
                        }}
                        className={styles.gird_link}
                    >
                        <Card
                            key={game.stableKey}
                            className={`${styles.card} ${styles[game.id]}`}
                            shadow="sm"
                            padding={0}
                            radius="md"
                        >
                            <Card.Section className={styles.card_section}>
                                <div className={styles.image_container}>
                                    {!game.loading ? (
                                        <ImageWithLazy
                                            key={game.cacheKey}
                                            gameId={game.id}
                                            src={game.cover_image}
                                            alt={game.name}
                                            className={styles.image}
                                        />
                                    ) : (
                                        <div className={styles.cover_loader}>
                                            <Loader color="blue" />
                                        </div>
                                    )}
                                </div>
                            </Card.Section>
                            <div className={`${styles.image_name}`}>
                                {game.name}
                            </div>
                        </Card>
                    </a>
                ))}
            </div>
            
            {/* 分页组件 */}
            {totalPages > 1 && isVisible && (
                <Group justify="center" style={{ marginTop: '2rem' }}>
                    <Pagination
                        value={currentPage}
                        total={totalPages}
                        onChange={onPageChange}
                        size="md"
                        color="blue"
                        radius="md"
                        getItemProps={(page) => ({
                            component: 'a',
                            href: hrefPagination(page),
                            onClick: (event) => {
                                event.preventDefault();
                                onPageChange(page);
                            },
                        })}
                    />
                </Group>
            )}
        </>
    );
}